import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

/**
 * IdleLogoutManager — single, app-level controller for browser inactivity logout.
 *
 * BROWSER-SIDE inactivity handling only (NOT server-enforced idle expiration):
 *  - Logs out after 20 min of TRUE user inactivity; warns at 18 min with a live countdown.
 *  - Genuine user input only (pointer/key/touch/wheel/click). Polling, token refresh, route
 *    mounts and visibility/focus changes never count as activity.
 *  - Cross-tab via a shared localStorage timestamp keyed to the current user id: activity in any
 *    tab keeps all tabs alive; mounting a tab never resets an existing timer.
 *  - Elapsed time is checked BEFORE accepting activity, so returning/clicking after the deadline
 *    logs out instead of reviving the session. Sleep/background resume rechecks immediately.
 *  - Reuses the existing logout operations (supabase.auth.signOut + POST /api/auth/logout).
 *
 * The authenticated user id is taken from the BACKEND session (GET /api/auth/me), which is the
 * authoritative auth path (legacy bcrypt cookie login as well as Supabase). The Supabase listener
 * is only a supplementary trigger to refresh that id. The /api/auth/me calls here are auth-state
 * checks and are NEVER treated as user activity.
 *
 * Thresholds are fixed: warning at 18 minutes, logout at 20 minutes.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ACTIVITY_KEY = 'st_idle_activity';        // { t: epochMs, uid }
const LOGOUT_BROADCAST_KEY = 'st_idle_logout';  // epochMs string
const TICK_MS = 1000;
const WRITE_THROTTLE_MS = 2000;
// mousemove and scroll are intentionally excluded (passive/programmatic noise, e.g. ScrollManager).
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'wheel', 'click'];

const WARN_MS = 18 * 60 * 1000;   // 1,080,000 ms
const LOGOUT_MS = 20 * 60 * 1000; // 1,200,000 ms

function readState() {
  try { return JSON.parse(window.localStorage.getItem(ACTIVITY_KEY) || 'null'); } catch { return null; }
}
function writeState(t, uid) {
  try { window.localStorage.setItem(ACTIVITY_KEY, JSON.stringify({ t, uid })); } catch { /* ignore */ }
}
function clearState() {
  try { window.localStorage.removeItem(ACTIVITY_KEY); } catch { /* ignore */ }
}

export default function IdleLogoutManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const [warnOpen, setWarnOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState(0);

  const uidRef = useRef(null);
  const prevUidRef = useRef(null);
  const initialResolvedRef = useRef(false);
  const loggingOutRef = useRef(false);
  const lastWriteRef = useRef(0);

  const doLogout = useCallback(async (opts = {}) => {
    const { broadcast = true, callRemote = true, callSignOut = true } = opts;
    if (loggingOutRef.current) return; // de-dupe requests/toasts within this tab
    loggingOutRef.current = true;
    setWarnOpen(false);
    clearState();
    if (broadcast) {
      try { window.localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now())); } catch { /* ignore */ }
    }
    try {
      if (callSignOut) await supabase.auth.signOut();        // clears any Supabase session (cross-tab)
      if (callRemote) await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch { /* navigate away regardless */ }
    uidRef.current = null;
    prevUidRef.current = null;
    toast('You were signed out due to inactivity.');
    navigate('/');
  }, [navigate]);

  // Resolve the authenticated user id from the backend session.
  const applyUid = useCallback((id) => {
    const prev = prevUidRef.current;
    if (!initialResolvedRef.current) {
      // First resolution (tab mount): adopt the shared window if one exists — never reset it.
      initialResolvedRef.current = true;
      uidRef.current = id;
      prevUidRef.current = id;
      if (id) {
        const st = readState();
        if (!st || st.uid !== id) writeState(Date.now(), id);
        loggingOutRef.current = false;
      }
      return;
    }
    // A signed-out -> signed-in transition after mount = a fresh login in THIS tab -> fresh window.
    if (id && prev == null) {
      writeState(Date.now(), id);
      loggingOutRef.current = false;
      setWarnOpen(false);
    }
    uidRef.current = id;
    prevUidRef.current = id;
  }, []);

  const refreshUid = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      const id = res.data?.user_id || res.data?.id || res.data?.email || null;
      applyUid(id || null);
    } catch {
      applyUid(null);
    }
  }, [applyUid]);

  // Accept genuine activity. Checks the deadline BEFORE reviving the session.
  const onActivity = useCallback(() => {
    const uid = uidRef.current;
    if (!uid || loggingOutRef.current) return;
    const now = Date.now();
    const st = readState();
    if (st && st.uid === uid && (now - st.t) >= LOGOUT_MS) {
      doLogout();
      return;
    }
    if (now - lastWriteRef.current >= WRITE_THROTTLE_MS) {
      lastWriteRef.current = now;
      writeState(now, uid);
    }
    setWarnOpen(false);
  }, [doLogout]);

  // Explicit "Stay signed in" — resets even inside the write throttle window.
  const staySignedIn = useCallback(() => {
    const uid = uidRef.current;
    if (!uid) return;
    const now = Date.now();
    const st = readState();
    if (st && st.uid === uid && (now - st.t) >= LOGOUT_MS) {
      doLogout();
      return;
    }
    lastWriteRef.current = now;
    writeState(now, uid);
    setWarnOpen(false);
  }, [doLogout]);

  // Resolve auth id at mount.
  useEffect(() => { refreshUid(); }, [refreshUid]);

  // Supplementary: refresh auth id when Supabase auth state changes (Supabase logins / sign-out).
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange(() => { refreshUid(); });
    return () => sub.subscription.unsubscribe();
  }, [refreshUid]);

  // Catch a legacy (cookie) login: on route change while unauthenticated, re-check the session.
  useEffect(() => {
    if (uidRef.current == null) refreshUid();
  }, [location.pathname, refreshUid]);

  // Ticking evaluator + listeners (genuine activity, cross-tab storage, sleep/resume).
  useEffect(() => {
    const tick = () => {
      const uid = uidRef.current;
      if (!uid || loggingOutRef.current) return;
      const now = Date.now();
      const st = readState();
      if (!st || st.uid !== uid) {
        // No shared window for this user yet (first authenticated load) — seed it, don't reset others.
        writeState(now, uid);
        setWarnOpen(false);
        return;
      }
      const elapsed = now - st.t;
      if (elapsed >= LOGOUT_MS) {
        doLogout();
      } else if (elapsed >= WARN_MS) {
        setWarnOpen(true);
        setRemainingMs(LOGOUT_MS - elapsed);
      } else {
        setWarnOpen(false);
      }
    };

    tick();
    const iv = setInterval(tick, TICK_MS);
    const onVis = () => { if (document.visibilityState === 'visible') { if (uidRef.current == null) refreshUid(); tick(); } };
    const onStorage = (ev) => {
      if (ev.key === LOGOUT_BROADCAST_KEY && ev.newValue) {
        // Another tab logged out due to inactivity — mirror sign-out, no duplicate network calls.
        doLogout({ broadcast: false, callRemote: false, callSignOut: false });
      } else if (ev.key === ACTIVITY_KEY) {
        tick(); // activity (or Stay signed in) in another tab — re-evaluate immediately
      }
    };

    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('storage', onStorage);
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('storage', onStorage);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [onActivity, doLogout, refreshUid]);

  const secs = Math.max(0, Math.ceil(remainingMs / 1000));
  const mm = Math.floor(secs / 60);
  const ss = String(secs % 60).padStart(2, '0');

  return (
    <AlertDialog open={warnOpen}>
      <AlertDialogContent data-testid="idle-warning-dialog" onEscapeKeyDown={(e) => e.preventDefault()}>
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="idle-warning-title">Are you still there?</AlertDialogTitle>
          <AlertDialogDescription data-testid="idle-warning-text">
            You&apos;ve been inactive for a while. For your security, you&apos;ll be signed out in{' '}
            <span data-testid="idle-countdown" className="font-semibold tabular-nums">{mm}:{ss}</span>.
            {' '}Any unsaved changes may be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" data-testid="idle-logout-now-btn" onClick={() => doLogout()}>
            Log out now
          </Button>
          <AlertDialogAction data-testid="idle-stay-signed-in-btn" onClick={staySignedIn}>
            Stay signed in
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
